import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Building2, ArrowLeft, Receipt, Users, UserPlus, Award, Briefcase, 
  Plus, Trash2, Edit2, TrendingUp, TrendingDown, Calendar, User,
  Search, Check, X, UserCog, Ban, ShieldOff, ChevronDown, ChevronUp, Eye,
  Wallet, Euro, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente per visualizzare un concorso con la lista degli iscritti
const ConcorsoCard = ({ concorso, onRefresh }) => {
  const [iscrizioni, setIscrizioni] = useState([]);
  const [showIscrizioni, setShowIscrizioni] = useState(false);
  const [loadingIscrizioni, setLoadingIscrizioni] = useState(false);

  const loadIscrizioni = async () => {
    if (!showIscrizioni) {
      setLoadingIscrizioni(true);
      try {
        const res = await axios.get(`${API_URL}/concorsi/${concorso.id}/iscrizioni`);
        setIscrizioni(res.data);
      } catch (error) {
        // Se non ha permessi, ignora silenziosamente
        setIscrizioni([]);
      } finally {
        setLoadingIscrizioni(false);
      }
    }
    setShowIscrizioni(!showIscrizioni);
  };

  return (
    <div className="p-4 border rounded-lg">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-semibold">{concorso.titolo}</h3>
          <p className="text-sm text-slate-600 mt-1">{concorso.descrizione}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={concorso.modalita === 'PRESENZA' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
            {concorso.modalita === 'PRESENZA' ? 'In Presenza' : 'Gestionale'}
          </Badge>
          <Badge className={concorso.stato === 'APERTO' ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>{concorso.stato}</Badge>
        </div>
      </div>
      <div className="flex gap-4 mt-3 text-sm text-slate-500">
        <span>{concorso.posti_disponibili} posti</span>
        {concorso.scadenza && <span>Scade: {new Date(concorso.scadenza).toLocaleDateString('it-IT')}</span>}
      </div>
      
      {/* Pulsante per vedere gli iscritti (solo per concorsi in PRESENZA) */}
      {concorso.modalita === 'PRESENZA' && (
        <div className="mt-4 pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={loadIscrizioni}
            className="w-full justify-between"
            data-testid={`toggle-iscrizioni-${concorso.id}`}
          >
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Vedi Iscritti ({iscrizioni.length || '...'})
            </span>
            {showIscrizioni ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          
          {showIscrizioni && (
            <div className="mt-3">
              {loadingIscrizioni ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900 mx-auto"></div>
                </div>
              ) : iscrizioni.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Nessun iscritto</p>
              ) : (
                <div className="space-y-2">
                  {iscrizioni.map((isc) => (
                    <div key={isc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium">@{isc.user_username}</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(isc.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GestionaleAzienda = () => {
  const { aziendaId } = useParams();
  const navigate = useNavigate();
  const { user, isSindacatoAdmin, hasPermesso } = useAuth();
  
  const [azienda, setAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scontrini');
  
  // Dati
  const [scontrini, setScontrini] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [concorsi, setConcorsi] = useState([]);
  const [domandeLavoro, setDomandeLavoro] = useState([]);
  const [ruoliAzienda, setRuoliAzienda] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersForBan, setAllUsersForBan] = useState([]);
  const [stipendi, setStipendi] = useState([]);
  const [oreDipendenti, setOreDipendenti] = useState([]);
  const [contoAzienda, setContoAzienda] = useState(null);
  
  // Dialog states
  const [scontrinoDialogOpen, setScontrinoDialogOpen] = useState(false);
  const [assunzioneDialogOpen, setAssunzioneDialogOpen] = useState(false);
  const [concorsoDialogOpen, setConcorsoDialogOpen] = useState(false);
  const [licenziaDialogOpen, setLicenziaDialogOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banForm, setBanForm] = useState({ motivo: '', durata_giorni: null, permanente: true });
  const [modificaRuoloDialogOpen, setModificaRuoloDialogOpen] = useState(false);
  const [stipendioDialogOpen, setStipendioDialogOpen] = useState(false);
  const [oreDialogOpen, setOreDialogOpen] = useState(false);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [prelievoDialogOpen, setPrelievoDialogOpen] = useState(false);
  const [selectedDipendente, setSelectedDipendente] = useState(null);
  const [motivoLicenziamento, setMotivoLicenziamento] = useState('');
  const [nuovoRuoloId, setNuovoRuoloId] = useState('');
  const [stipendioForm, setStipendioForm] = useState({ user_id: '', importo: '', note: '' });
  const [oreForm, setOreForm] = useState({ user_id: '', ore: 0, minuti: 0 });
  const [warnForm, setWarnForm] = useState({ user_id: '', motivo: '' });
  const [prelievoForm, setPrelievoForm] = useState({ importo: '', descrizione: '', note: '' });
  
  // Forms
  const [scontrinoForm, setScontrinoForm] = useState({
    tipo: 'ENTRATA',
    importo: 0,
    descrizione: '',
    data_operazione: new Date().toISOString().split('T')[0]
  });
  const [assunzioneForm, setAssunzioneForm] = useState({
    user_id: '',
    ruolo_azienda_id: ''
  });
  const [concorsoForm, setConcorsoForm] = useState({
    titolo: '',
    descrizione: '',
    requisiti: '',
    posti_disponibili: 1,
    scadenza: '',
    modalita: 'GESTIONALE'
  });
  const [searchUser, setSearchUser] = useState('');
  const [searchAllUsers, setSearchAllUsers] = useState('');

  const isAdmin = isSindacatoAdmin();
  
  // Verifica se l'utente ha accesso al gestionale di questa azienda
  // Solo: Admin, chi ha permesso visualizza_gestionale_azienda, o chi ha livello >= 9 in questa azienda
  const getUserLevelInAzienda = () => {
    if (isAdmin) return 10;
    const impiego = user?.impieghi?.find(imp => imp.azienda_id === aziendaId);
    return impiego?.livello_gerarchia || 0;
  };
  const userLevel = getUserLevelInAzienda();
  const canManage = isAdmin || hasPermesso('visualizza_gestionale_azienda') || userLevel >= 9;
  
  // Per il ban: Admin o permesso gestisci_ban
  const canBan = isAdmin || hasPermesso('gestisci_ban');

  // Verifica se l'azienda corrente è "Staff"
  const isStaffAzienda = azienda?.nome?.toLowerCase().includes('staff');
  
  // Mostra tab Ban per Staff se è admin o ha il permesso
  const showBanTab = isStaffAzienda && (isAdmin || canBan);
  
  // Verifica se è un'azienda di servizio pubblico (Banca, Ospedale, Scuola, etc.)
  const AZIENDE_SERVIZIO_PUBBLICO = ['BANCA', 'OSPEDALE', 'SCUOLA', 'FORZE_ORDINE', 'POLIZIA', 'POSTA', 'NEGOZIO'];
  const isServizioPublico = azienda?.tipo && AZIENDE_SERVIZIO_PUBBLICO.includes(azienda.tipo.toUpperCase());
  
  // Stato per gli utenti visibili ai servizi pubblici
  const [allUsersForService, setAllUsersForService] = useState([]);
  const [searchServiceUsers, setSearchServiceUsers] = useState('');
  
  // Stato per TUTTI i cittadini (per assunzioni)
  const [allCittadini, setAllCittadini] = useState([]);

  useEffect(() => {
    if (aziendaId) {
      fetchAziendaData();
    }
  }, [aziendaId]);

  const fetchAziendaData = async () => {
    try {
      // Carica dati azienda
      const azRes = await axios.get(`${API_URL}/aziende/${aziendaId}`);
      setAzienda(azRes.data);
      
      // Carica tutti i dati in parallelo
      const [scontRes, usersRes, cittadiniRes, concorsiRes, ldlRes, ruoliRes, stipendiRes] = await Promise.all([
        axios.get(`${API_URL}/scontrini?azienda_id=${aziendaId}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/users`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/all-users-complete`).catch(() => ({ data: [] })), // TUTTI gli utenti per assunzioni
        axios.get(`${API_URL}/concorsi`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/ldl`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/ruoli-azienda?azienda_id=${aziendaId}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/stipendi?azienda_id=${aziendaId}`).catch(() => ({ data: [] }))
      ]);
      
      setScontrini(scontRes.data.filter(s => s.azienda_id === aziendaId));
      setAllUsers(usersRes.data);
      setAllCittadini(cittadiniRes.data); // TUTTI i cittadini
      setDipendenti(usersRes.data.filter(u => 
        u.azienda_id === aziendaId || u.impieghi?.some(imp => imp.azienda_id === aziendaId)
      ));
      setConcorsi(concorsiRes.data.filter(c => c.azienda_id === aziendaId));
      setDomandeLavoro(ldlRes.data.filter(d => d.azienda_id === aziendaId));
      setRuoliAzienda(ruoliRes.data);
      setStipendi(stipendiRes.data);
      
      // Se è l'azienda Staff, carica SEMPRE tutti gli utenti per il ban
      if (azRes.data?.nome?.toLowerCase().includes('staff')) {
        try {
          const allUsersForBanRes = await axios.get(`${API_URL}/all-users-for-ban`);
          setAllUsersForBan(allUsersForBanRes.data);
        } catch {
          // Non ha permesso gestisci_ban - prova comunque con endpoint normale
          setAllUsersForBan(usersRes.data);
        }
      }
      
      // Se è un'azienda di servizio pubblico, carica tutti gli utenti
      const SERVIZI = ['BANCA', 'OSPEDALE', 'SCUOLA', 'FORZE_ORDINE', 'POLIZIA', 'POSTA', 'NEGOZIO'];
      if (azRes.data?.tipo && SERVIZI.includes(azRes.data.tipo.toUpperCase())) {
        try {
          const allUsersServiceRes = await axios.get(`${API_URL}/all-users-service`);
          setAllUsersForService(allUsersServiceRes.data);
        } catch {
          setAllUsersForService([]);
        }
      }
      
      // Carica ore dipendenti
      try {
        const oreRes = await axios.get(`${API_URL}/ore-lavoro/dipendenti/${aziendaId}`);
        setOreDipendenti(oreRes.data);
      } catch {
        setOreDipendenti([]);
      }
      
      // Carica conto azienda
      try {
        const contoRes = await axios.get(`${API_URL}/banca/conto-azienda/${aziendaId}`);
        setContoAzienda(contoRes.data);
      } catch {
        setContoAzienda(null);
      }
      
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // === ORE LAVORO ===
  const openOreDialog = (dipendente = null) => {
    setOreForm({
      user_id: dipendente?.id || '',
      ore: 0,
      minuti: 0
    });
    setOreDialogOpen(true);
  };

  const handleRegistraOre = async () => {
    if (!oreForm.user_id) {
      toast.error('Seleziona un dipendente');
      return;
    }
    try {
      await axios.post(`${API_URL}/ore-lavoro/registra`, {
        user_id: oreForm.user_id,
        ore: parseInt(oreForm.ore) || 0,
        minuti: parseInt(oreForm.minuti) || 0,
        azienda_id: aziendaId
      });
      toast.success('Ore registrate!');
      setOreDialogOpen(false);
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // === WARN ===
  const openWarnDialog = (utente = null) => {
    setWarnForm({
      user_id: utente?.id || '',
      motivo: ''
    });
    setWarnDialogOpen(true);
  };

  const handleDaiWarn = async () => {
    if (!warnForm.user_id || !warnForm.motivo) {
      toast.error('Seleziona utente e inserisci motivo');
      return;
    }
    try {
      await axios.post(`${API_URL}/warn`, {
        user_id: warnForm.user_id,
        motivo: warnForm.motivo
      });
      toast.success('Warn dato!');
      setWarnDialogOpen(false);
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // === PRELIEVO BANCA ===
  const handlePrelievo = async () => {
    if (!prelievoForm.importo || !prelievoForm.descrizione) {
      toast.error('Inserisci importo e descrizione');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/banca/prelievo-azienda/${aziendaId}`, {
        importo: parseFloat(prelievoForm.importo),
        descrizione: prelievoForm.descrizione,
        note: prelievoForm.note || null
      });
      toast.success(res.data.message);
      setPrelievoDialogOpen(false);
      setPrelievoForm({ importo: '', descrizione: '', note: '' });
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel prelievo');
    }
  };

  // === STIPENDI ===
  const openStipendioDialog = (dipendente = null) => {
    setStipendioForm({
      user_id: dipendente?.id || '',
      importo: '',
      note: ''
    });
    setStipendioDialogOpen(true);
  };

  const handlePagaStipendio = async () => {
    if (!stipendioForm.user_id || !stipendioForm.importo) {
      toast.error('Seleziona dipendente e importo');
      return;
    }
    try {
      await axios.post(`${API_URL}/stipendi?azienda_id=${aziendaId}`, {
        user_id: stipendioForm.user_id,
        importo: parseFloat(stipendioForm.importo),
        note: stipendioForm.note || null
      });
      toast.success('Stipendio pagato con successo!');
      setStipendioDialogOpen(false);
      setStipendioForm({ user_id: '', importo: '', note: '' });
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
    }
  };

  // === SCONTRINI ===
  const handleCreateScontrino = async () => {
    if (!scontrinoForm.descrizione || !scontrinoForm.importo) {
      toast.error('Compila tutti i campi');
      return;
    }
    try {
      await axios.post(`${API_URL}/scontrini`, {
        ...scontrinoForm,
        azienda_id: aziendaId,
        importo: parseFloat(scontrinoForm.importo)
      });
      toast.success('Scontrino creato');
      setScontrinoDialogOpen(false);
      setScontrinoForm({ tipo: 'ENTRATA', importo: 0, descrizione: '', data_operazione: new Date().toISOString().split('T')[0] });
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteScontrino = async (id) => {
    if (!confirm('Eliminare questo scontrino?')) return;
    try {
      await axios.delete(`${API_URL}/scontrini/${id}`);
      toast.success('Scontrino eliminato');
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // === ASSUNZIONI ===
  const handleAssumi = async () => {
    if (!assunzioneForm.user_id || !assunzioneForm.ruolo_azienda_id) {
      toast.error('Seleziona utente e ruolo');
      return;
    }
    try {
      await axios.post(`${API_URL}/assunzioni?user_id=${assunzioneForm.user_id}&azienda_id=${aziendaId}&ruolo_azienda_id=${assunzioneForm.ruolo_azienda_id}`);
      toast.success('Utente assunto');
      setAssunzioneDialogOpen(false);
      setAssunzioneForm({ user_id: '', ruolo_azienda_id: '' });
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openLicenziaDialog = (dipendente) => {
    setSelectedDipendente(dipendente);
    setMotivoLicenziamento('');
    setLicenziaDialogOpen(true);
  };

  const handleLicenzia = async () => {
    if (!selectedDipendente) return;
    try {
      await axios.post(`${API_URL}/assunzioni/licenzia/${selectedDipendente.id}/${aziendaId}`, { 
        motivo: motivoLicenziamento || null 
      });
      toast.success('Dipendente licenziato');
      setLicenziaDialogOpen(false);
      setSelectedDipendente(null);
      setMotivoLicenziamento('');
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openModificaRuoloDialog = (dipendente) => {
    setSelectedDipendente(dipendente);
    const impiego = dipendente.impieghi?.find(imp => imp.azienda_id === aziendaId);
    setNuovoRuoloId(impiego?.ruolo_azienda_id || '');
    setModificaRuoloDialogOpen(true);
  };

  const handleModificaRuolo = async () => {
    if (!selectedDipendente || !nuovoRuoloId) {
      toast.error('Seleziona un ruolo');
      return;
    }
    try {
      // Usa l'endpoint di assunzione per aggiornare il ruolo
      await axios.post(`${API_URL}/assunzioni?user_id=${selectedDipendente.id}&azienda_id=${aziendaId}&ruolo_azienda_id=${nuovoRuoloId}`);
      toast.success('Ruolo aggiornato');
      setModificaRuoloDialogOpen(false);
      setSelectedDipendente(null);
      setNuovoRuoloId('');
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // === BAN ===
  const openBanDialog = (u) => {
    setSelectedDipendente(u);
    setBanForm({ motivo: '', durata_giorni: null, permanente: true });
    setBanDialogOpen(true);
  };

  const handleBan = async () => {
    if (!banForm.motivo.trim()) {
      toast.error('Inserisci un motivo per il ban');
      return;
    }
    try {
      await axios.post(`${API_URL}/ban/${selectedDipendente.id}`, {
        motivo: banForm.motivo,
        durata_giorni: banForm.permanente ? null : banForm.durata_giorni
      });
      toast.success(`@${selectedDipendente.username} è stato bannato`);
      setBanDialogOpen(false);
      setSelectedDipendente(null);
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ban');
    }
  };

  const handleUnban = async (userId) => {
    try {
      await axios.delete(`${API_URL}/ban/${userId}`);
      toast.success('Ban rimosso');
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione del ban');
    }
  };

  // === CONCORSI ===
  const handleCreateConcorso = async () => {
    if (!concorsoForm.titolo) {
      toast.error('Inserisci un titolo');
      return;
    }
    try {
      await axios.post(`${API_URL}/concorsi`, {
        ...concorsoForm,
        azienda_id: aziendaId
      });
      toast.success('Concorso creato');
      setConcorsoDialogOpen(false);
      setConcorsoForm({ titolo: '', descrizione: '', requisiti: '', posti_disponibili: 1, scadenza: '', modalita: 'GESTIONALE' });
      fetchAziendaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Filtro utenti per ban
  const filteredUsersForBan = allUsersForBan.filter(u => 
    u.username?.toLowerCase().includes(searchAllUsers.toLowerCase()) ||
    u.nome?.toLowerCase().includes(searchAllUsers.toLowerCase())
  );

  // Calcoli
  const totaleEntrate = scontrini.filter(s => s.tipo === 'ENTRATA').reduce((sum, s) => sum + (s.importo || 0), 0);
  const totaleUscite = scontrini.filter(s => s.tipo === 'USCITA').reduce((sum, s) => sum + (s.importo || 0), 0);
  const saldo = totaleEntrate - totaleUscite;

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  // Filtro utenti per assunzioni - mostra TUTTI gli utenti (disoccupati e occupati)
  const filteredUsers = allCittadini.filter(u => 
    u.ruolo !== 'SINDACATO_ADMIN' &&
    (u.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
     u.nome?.toLowerCase().includes(searchUser.toLowerCase()) ||
     u.cognome?.toLowerCase().includes(searchUser.toLowerCase()))
  );
  
  // Verifica se un utente è già dipendente di questa azienda
  const isGiaDipendente = (userId) => dipendenti.some(d => d.id === userId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!azienda) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Azienda non trovata</p>
        <Button onClick={() => navigate('/gestione-aziende')} className="mt-4">
          Torna indietro
        </Button>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Non hai i permessi per accedere a questo gestionale</p>
        <Button onClick={() => navigate('/gestione-aziende')} className="mt-4">
          Torna indietro
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="gestionale-azienda-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/gestione-aziende')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alle aziende
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
            <Building2 className="w-8 h-8 text-slate-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{azienda.nome}</h1>
            <Badge className="mt-1">{azienda.tipo}</Badge>
          </div>
        </div>
        
        {/* Stats rapide */}
        <div className="flex gap-4 flex-wrap">
          <div className="text-center px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className={`text-2xl font-bold ${(contoAzienda?.saldo || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(contoAzienda?.saldo || 0)}
            </p>
            <p className="text-xs text-emerald-600">Conto Banca</p>
          </div>
          <div className="text-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-2xl font-bold text-blue-700">{dipendenti.length}</p>
            <p className="text-xs text-blue-600">Dipendenti</p>
          </div>
          <div className="text-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totaleEntrate)}</p>
            <p className="text-xs text-green-600">Entrate</p>
          </div>
          <div className="text-center px-4 py-2 bg-red-50 rounded-lg border border-red-200">
            <p className="text-2xl font-bold text-red-700">{formatCurrency(totaleUscite)}</p>
            <p className="text-xs text-red-600">Uscite</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-flex flex-wrap">
          <TabsTrigger value="scontrini" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Scontrini</span>
          </TabsTrigger>
          <TabsTrigger value="dipendenti" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Dipendenti</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="ore" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Ore Online</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="stipendi" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            <span className="hidden sm:inline">Stipendi</span>
          </TabsTrigger>
          <TabsTrigger value="assunzioni" className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Assunzioni</span>
          </TabsTrigger>
          <TabsTrigger value="concorsi" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            <span className="hidden sm:inline">Concorsi</span>
          </TabsTrigger>
          <TabsTrigger value="domande" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Domande</span>
          </TabsTrigger>
          {isServizioPublico && (
            <TabsTrigger value="cittadini" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Cittadini</span>
            </TabsTrigger>
          )}
          {showBanTab && (
            <TabsTrigger value="tutti-utenti" className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              <span className="hidden sm:inline">Tutti Utenti</span>
            </TabsTrigger>
          )}
          {showBanTab && (
            <TabsTrigger value="warn" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Warn</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* === TAB SCONTRINI === */}
        <TabsContent value="scontrini" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Scontrini ({scontrini.length})
              </CardTitle>
              <div className="flex gap-2">
                {contoAzienda && (
                  <Dialog open={prelievoDialogOpen} onOpenChange={setPrelievoDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                        <Wallet className="w-4 h-4 mr-2" />
                        Preleva da Banca
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Prelievo da Conto Aziendale</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="p-3 bg-purple-50 rounded-lg text-center">
                          <p className="text-sm text-purple-600">Saldo disponibile</p>
                          <p className="text-2xl font-bold text-purple-700">{formatCurrency(contoAzienda.saldo || 0)}</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Importo (€) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={prelievoForm.importo}
                            onChange={(e) => setPrelievoForm({...prelievoForm, importo: e.target.value})}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Descrizione *</Label>
                          <Input
                            value={prelievoForm.descrizione}
                            onChange={(e) => setPrelievoForm({...prelievoForm, descrizione: e.target.value})}
                            placeholder="Motivo del prelievo..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Note (opzionale)</Label>
                          <Textarea
                            value={prelievoForm.note}
                            onChange={(e) => setPrelievoForm({...prelievoForm, note: e.target.value})}
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setPrelievoDialogOpen(false)}>Annulla</Button>
                          <Button onClick={handlePrelievo} className="bg-purple-600 hover:bg-purple-700">
                            <Wallet className="w-4 h-4 mr-2" />
                            Preleva
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                <Dialog open={scontrinoDialogOpen} onOpenChange={setScontrinoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuovo Scontrino
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nuovo Scontrino</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select value={scontrinoForm.tipo} onValueChange={(v) => setScontrinoForm({...scontrinoForm, tipo: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENTRATA">Entrata</SelectItem>
                          <SelectItem value="USCITA">Uscita</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Importo (€)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={scontrinoForm.importo}
                        onChange={(e) => setScontrinoForm({...scontrinoForm, importo: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Input 
                        value={scontrinoForm.descrizione}
                        onChange={(e) => setScontrinoForm({...scontrinoForm, descrizione: e.target.value})}
                        placeholder="Descrizione operazione..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input 
                        type="date"
                        value={scontrinoForm.data_operazione}
                        onChange={(e) => setScontrinoForm({...scontrinoForm, data_operazione: e.target.value})}
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setScontrinoDialogOpen(false)}>Annulla</Button>
                      <Button onClick={handleCreateScontrino}>Salva</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Riepilogo */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">{formatCurrency(totaleEntrate)}</p>
                  <p className="text-xs text-green-600">Entrate</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <TrendingDown className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-red-700">{formatCurrency(totaleUscite)}</p>
                  <p className="text-xs text-red-600">Uscite</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${(contoAzienda?.saldo || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <Wallet className={`w-6 h-6 mx-auto mb-1 ${(contoAzienda?.saldo || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                  <p className={`text-lg font-bold ${(contoAzienda?.saldo || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(contoAzienda?.saldo || 0)}</p>
                  <p className={`text-xs ${(contoAzienda?.saldo || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Conto Banca</p>
                </div>
              </div>

              {scontrini.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessuno scontrino registrato</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scontrini.slice(0, 50).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="text-sm text-slate-600">
                            {new Date(s.data_operazione || s.created_at).toLocaleDateString('it-IT')}
                          </TableCell>
                          <TableCell>{s.descrizione}</TableCell>
                          <TableCell>
                            <Badge className={s.tipo === 'ENTRATA' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {s.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${s.tipo === 'ENTRATA' ? 'text-green-600' : 'text-red-600'}`}>
                            {s.tipo === 'ENTRATA' ? '+' : '-'}{formatCurrency(s.importo)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteScontrino(s.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB DIPENDENTI === */}
        <TabsContent value="dipendenti" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Dipendenti ({dipendenti.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dipendenti.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun dipendente</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nickname</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Ore Settimanali</TableHead>
                        <TableHead>Data Assunzione</TableHead>
                        <TableHead>Tempo Servizio</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dipendenti.map((d) => {
                        const impiego = d.impieghi?.find(imp => imp.azienda_id === aziendaId);
                        // Trova data assunzione dallo storico per questa azienda
                        const assunzioneEntry = d.storico_assunzioni?.find(s => 
                          s.azienda_id === aziendaId && s.tipo === 'ASSUNZIONE'
                        );
                        const dataAssunzione = assunzioneEntry?.created_at || impiego?.data_assunzione || d.created_at;
                        
                        // Trova ore settimanali del dipendente
                        const oreDip = oreDipendenti.find(o => o.id === d.id);
                        const oreSettStr = oreDip 
                          ? `${oreDip.ore_settimanali || 0}h ${oreDip.minuti_settimanali || 0}m`
                          : '0h 0m';
                        
                        // Calcola tempo di servizio
                        const calcolaTempoServizio = (dataStr) => {
                          if (!dataStr) return 'N/D';
                          const data = new Date(dataStr);
                          const oggi = new Date();
                          const diffMs = oggi - data;
                          const giorni = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                          if (giorni < 30) return `${giorni} giorni`;
                          const mesi = Math.floor(giorni / 30);
                          if (mesi < 12) return `${mesi} mesi`;
                          const anni = Math.floor(mesi / 12);
                          const mesiRestanti = mesi % 12;
                          return mesiRestanti > 0 ? `${anni}a ${mesiRestanti}m` : `${anni} anni`;
                        };
                        
                        return (
                          <TableRow key={d.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                  <User className="w-4 h-4 text-slate-600" />
                                </div>
                                <span className="font-medium">@{d.username}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {impiego?.ruolo_nome || d.ruolo_azienda_nome || 'Dipendente'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-cyan-100 text-cyan-700">
                                <Clock className="w-3 h-3 mr-1" />
                                {oreSettStr}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-600">
                                {dataAssunzione 
                                  ? new Date(dataAssunzione).toLocaleDateString('it-IT', {day: '2-digit', month: 'short', year: 'numeric'})
                                  : 'N/D'
                                }
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-700">
                                {calcolaTempoServizio(dataAssunzione)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {d.ruolo !== 'SINDACATO_ADMIN' && d.id !== user?.id && (
                                  <>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-blue-600 hover:bg-blue-50"
                                      onClick={() => openModificaRuoloDialog(d)}
                                      title="Modifica ruolo"
                                    >
                                      <UserCog className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-red-600 hover:bg-red-50"
                                      onClick={() => openLicenziaDialog(d)}
                                      title="Licenzia con motivo"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB ORE ONLINE (Solo Admin) === */}
        {isAdmin && (
        <TabsContent value="ore" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Ore Online Dipendenti
              </CardTitle>
              <Button onClick={() => openOreDialog()} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Registra Ore
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Le ore settimanali si azzerano ogni settimana. Le ore totali sono cumulative.
              </p>
              
              {oreDipendenti.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun dipendente trovato</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dipendente</TableHead>
                        <TableHead className="text-center">Ore Settimanali</TableHead>
                        <TableHead className="text-center">Ore Totali</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {oreDipendenti.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium">@{d.username}</p>
                                <p className="text-xs text-slate-500">{d.nome} {d.cognome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-700">
                              {d.ore_settimanali}h {d.minuti_settimanali}m
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-cyan-100 text-cyan-700">
                              {d.ore_totali}h {d.minuti_totali}m
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openOreDialog(d)}
                              className="text-blue-600"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Aggiungi
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* === TAB STIPENDI === */}
        <TabsContent value="stipendi" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Gestione Stipendi
              </CardTitle>
              <Button onClick={() => openStipendioDialog()} className="bg-green-600 hover:bg-green-700">
                <Euro className="w-4 h-4 mr-2" />
                Paga Stipendio
              </Button>
            </CardHeader>
            <CardContent>
              {/* Calcoli stipendi */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-green-700">
                    {formatCurrency(stipendi.reduce((sum, s) => sum + (s.importo || 0), 0))}
                  </p>
                  <p className="text-xs text-green-600">Totale Pagato</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-blue-700">{stipendi.length}</p>
                  <p className="text-xs text-blue-600">Pagamenti Effettuati</p>
                </div>
              </div>

              {/* Lista pagamenti rapidi ai dipendenti */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Paga rapidamente un dipendente:</h3>
                <div className="flex flex-wrap gap-2">
                  {dipendenti.map((d) => (
                    <Button
                      key={d.id}
                      variant="outline"
                      size="sm"
                      onClick={() => openStipendioDialog(d)}
                      className="text-green-700 border-green-200 hover:bg-green-50"
                    >
                      <Euro className="w-3 h-3 mr-1" />
                      @{d.username}
                    </Button>
                  ))}
                  {dipendenti.length === 0 && (
                    <p className="text-sm text-slate-500">Nessun dipendente da pagare</p>
                  )}
                </div>
              </div>

              {/* Storico stipendi */}
              <h3 className="text-sm font-medium text-slate-700 mb-3">Storico pagamenti:</h3>
              {stipendi.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessuno stipendio pagato</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead>Pagato da</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stipendi.slice(0, 50).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="text-sm text-slate-600">
                            {new Date(s.created_at).toLocaleDateString('it-IT')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                                <User className="w-3 h-3 text-slate-600" />
                              </div>
                              <span className="font-medium">@{s.user_username}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(s.importo)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                            {s.note || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {s.pagato_da_nome}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB ASSUNZIONI === */}
        <TabsContent value="assunzioni" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Assumi Nuovo Dipendente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Cerca utente per nickname</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    placeholder="@nickname..."
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utente</Label>
                  <Select value={assunzioneForm.user_id} onValueChange={(v) => setAssunzioneForm({...assunzioneForm, user_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent className="max-h-64 overflow-y-auto">
                      {filteredUsers.map((u) => {
                        const giaDipendente = isGiaDipendente(u.id);
                        const haAltriImpieghi = u.impieghi && u.impieghi.length > 0;
                        return (
                          <SelectItem 
                            key={u.id} 
                            value={u.id}
                            disabled={giaDipendente}
                            className={giaDipendente ? 'opacity-50' : ''}
                          >
                            <div className="flex items-center gap-2">
                              <span>@{u.username} - {u.nome} {u.cognome}</span>
                              {giaDipendente && <Badge className="bg-green-100 text-green-800 text-xs ml-1">Già assunto qui</Badge>}
                              {!giaDipendente && haAltriImpieghi && <Badge className="bg-blue-100 text-blue-800 text-xs ml-1">Lavora altrove</Badge>}
                              {!giaDipendente && !haAltriImpieghi && <Badge className="bg-slate-100 text-slate-600 text-xs ml-1">Disoccupato</Badge>}
                            </div>
                          </SelectItem>
                        );
                      })}
                      {filteredUsers.length === 0 && (
                        <div className="px-2 py-3 text-sm text-slate-500 text-center">
                          {allCittadini.length === 0 ? 'Caricamento...' : 'Nessun utente trovato'}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select value={assunzioneForm.ruolo_azienda_id} onValueChange={(v) => setAssunzioneForm({...assunzioneForm, ruolo_azienda_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {ruoliAzienda.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleAssumi} className="bg-green-600 hover:bg-green-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Assumi
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB CONCORSI === */}
        <TabsContent value="concorsi" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Concorsi ({concorsi.length})
              </CardTitle>
              <Dialog open={concorsoDialogOpen} onOpenChange={setConcorsoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Concorso
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Nuovo Concorso</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Titolo</Label>
                      <Input 
                        value={concorsoForm.titolo}
                        onChange={(e) => setConcorsoForm({...concorsoForm, titolo: e.target.value})}
                        placeholder="Titolo del concorso"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Textarea 
                        value={concorsoForm.descrizione}
                        onChange={(e) => setConcorsoForm({...concorsoForm, descrizione: e.target.value})}
                        placeholder="Descrizione..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Posti disponibili</Label>
                        <Input 
                          type="number"
                          min="1"
                          value={concorsoForm.posti_disponibili}
                          onChange={(e) => setConcorsoForm({...concorsoForm, posti_disponibili: parseInt(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Scadenza</Label>
                        <Input 
                          type="date"
                          value={concorsoForm.scadenza}
                          onChange={(e) => setConcorsoForm({...concorsoForm, scadenza: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Modalità</Label>
                      <Select value={concorsoForm.modalita} onValueChange={(v) => setConcorsoForm({...concorsoForm, modalita: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GESTIONALE">Via Gestionale (Curriculum)</SelectItem>
                          <SelectItem value="PRESENZA">In Presenza (Iscrizione diretta)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        {concorsoForm.modalita === 'GESTIONALE' 
                          ? 'I candidati invieranno il curriculum tramite il gestionale' 
                          : 'I candidati si iscriveranno direttamente e si presenteranno di persona'}
                      </p>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setConcorsoDialogOpen(false)}>Annulla</Button>
                      <Button onClick={handleCreateConcorso}>Crea Concorso</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {concorsi.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun concorso attivo</p>
              ) : (
                <div className="space-y-4">
                  {concorsi.map((c) => (
                    <ConcorsoCard key={c.id} concorso={c} onRefresh={fetchAziendaData} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB DOMANDE DI LAVORO === */}
        <TabsContent value="domande" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Domande di Lavoro ({domandeLavoro.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {domandeLavoro.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessuna domanda di lavoro</p>
              ) : (
                <div className="space-y-4">
                  {domandeLavoro.map((d) => (
                    <div key={d.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">@{d.candidato_username || d.candidato_nome}</p>
                          <p className="text-sm text-slate-600">{d.messaggio}</p>
                        </div>
                        <Badge className={
                          d.stato === 'IN_ATTESA' ? 'bg-amber-100 text-amber-800' :
                          d.stato === 'ACCETTATA' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }>{d.stato}</Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(d.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TAB CITTADINI (Per aziende di servizio pubblico) === */}
        {isServizioPublico && (
          <TabsContent value="cittadini" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Lista Cittadini ({allUsersForService.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchServiceUsers}
                    onChange={(e) => setSearchServiceUsers(e.target.value)}
                    placeholder="Cerca per nickname..."
                    className="pl-10"
                  />
                </div>
                {allUsersForService.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun cittadino trovato</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nickname</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Registrato il</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allUsersForService
                          .filter(u => 
                            u.username?.toLowerCase().includes(searchServiceUsers.toLowerCase()) ||
                            u.nome?.toLowerCase().includes(searchServiceUsers.toLowerCase()) ||
                            u.cognome?.toLowerCase().includes(searchServiceUsers.toLowerCase())
                          )
                          .slice(0, 100)
                          .map((u) => (
                            <TableRow key={u.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-slate-600" />
                                  </div>
                                  <span className="font-medium">@{u.username}</span>
                                </div>
                              </TableCell>
                              <TableCell>{u.nome} {u.cognome}</TableCell>
                              <TableCell className="text-sm text-slate-500">
                                {new Date(u.created_at).toLocaleDateString('it-IT')}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* === TAB TUTTI GLI UTENTI (Solo per Staff con permesso gestisci_ban) === */}
        {showBanTab && (
          <TabsContent value="tutti-utenti" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ban className="w-5 h-5" />
                  Gestione Ban ({allUsersForBan.length} utenti)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchAllUsers}
                    onChange={(e) => setSearchAllUsers(e.target.value)}
                    placeholder="Cerca per nickname..."
                    className="pl-10"
                  />
                </div>
                {filteredUsersForBan.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun utente trovato</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nickname</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead>Dettagli Ban</TableHead>
                          <TableHead>Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsersForBan.slice(0, 50).map((u) => (
                          <TableRow key={u.id} className={u.ban_info?.is_banned ? 'bg-red-50' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.ban_info?.is_banned ? 'bg-red-200' : 'bg-slate-200'}`}>
                                  <User className="w-4 h-4 text-slate-600" />
                                </div>
                                <span className="font-medium">@{u.username}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {u.ban_info?.is_banned ? (
                                <Badge className="bg-red-600 text-white">BANNATO</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">Attivo</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.ban_info?.is_banned ? (
                                <div className="text-sm space-y-1">
                                  <div className="flex items-center gap-1 text-slate-600">
                                    <span className="font-medium text-red-700">Bannato da:</span>
                                    <span>{u.ban_info.banned_by_nome || 'Staff'}</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-slate-600">
                                    <span className="font-medium">Motivo:</span>
                                    <span className="italic">{u.ban_info.motivo || 'Non specificato'}</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-slate-600">
                                    <span className="font-medium">Durata:</span>
                                    <span>
                                      {u.ban_info.permanente || !u.ban_info.data_scadenza 
                                        ? <Badge variant="destructive" className="text-xs">Permanente</Badge>
                                        : `Fino al ${new Date(u.ban_info.data_scadenza).toLocaleDateString('it-IT')}`
                                      }
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && (
                                u.ban_info?.is_banned ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:bg-green-50"
                                    onClick={() => handleUnban(u.id)}
                                    title="Rimuovi ban"
                                  >
                                    <ShieldOff className="w-4 h-4 mr-1" />
                                    Sbanna
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => openBanDialog(u)}
                                    title="Banna utente"
                                  >
                                    <Ban className="w-4 h-4 mr-1" />
                                    Banna
                                  </Button>
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* === TAB WARN (Solo Staff) === */}
        {showBanTab && (
          <TabsContent value="warn" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  Sistema Warn
                </CardTitle>
                <Button onClick={() => openWarnDialog()} className="bg-amber-600 hover:bg-amber-700">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Dai Warn
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  I warn vengono visualizzati dall'utente come: "Sei stato warnato da [nome] per: [motivo]"
                </p>
                
                {/* Lista utenti con warn */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utente</TableHead>
                        <TableHead className="text-center">Warn Attivi</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allUsersForBan.filter(u => u.warns?.length > 0).map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium">@{u.username}</p>
                                <p className="text-xs text-slate-500">{u.nome} {u.cognome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-amber-100 text-amber-700">
                              {u.warns?.length || 0} warn
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openWarnDialog(u)}
                              className="text-amber-600"
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Aggiungi Warn
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {allUsersForBan.filter(u => u.warns?.length > 0).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                            Nessun utente con warn attivi
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Registra Ore Online */}
      <Dialog open={oreDialogOpen} onOpenChange={setOreDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-600 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Registra Ore Online
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Dipendente</Label>
              <Select value={oreForm.user_id} onValueChange={(v) => setOreForm({...oreForm, user_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona dipendente..." />
                </SelectTrigger>
                <SelectContent>
                  {dipendenti.map((d) => (
                    <SelectItem key={d.id} value={d.id}>@{d.username} - {d.nome} {d.cognome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ore</Label>
                <Input
                  type="number"
                  min="0"
                  value={oreForm.ore}
                  onChange={(e) => setOreForm({...oreForm, ore: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Minuti</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={oreForm.minuti}
                  onChange={(e) => setOreForm({...oreForm, minuti: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setOreDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleRegistraOre} className="bg-blue-600 hover:bg-blue-700">
                <Clock className="w-4 h-4 mr-2" />
                Registra
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Warn */}
      <Dialog open={warnDialogOpen} onOpenChange={setWarnDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Dai Warn
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Utente</Label>
              <Select value={warnForm.user_id} onValueChange={(v) => setWarnForm({...warnForm, user_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona utente..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {allUsersForBan.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      @{u.username} - {u.nome} {u.cognome}
                      {u.warns?.length > 0 && ` (${u.warns.length} warn)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo del Warn *</Label>
              <Textarea
                value={warnForm.motivo}
                onChange={(e) => setWarnForm({...warnForm, motivo: e.target.value})}
                placeholder="Descrivi il motivo del warn..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setWarnDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleDaiWarn} className="bg-amber-600 hover:bg-amber-700">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Dai Warn
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Licenziamento con Motivo */}
      <Dialog open={licenziaDialogOpen} onOpenChange={setLicenziaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Licenzia Dipendente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Stai per licenziare <strong>@{selectedDipendente?.username}</strong> da <strong>{azienda?.nome}</strong>.
            </p>
            <div className="space-y-2">
              <Label>Motivo del Licenziamento (opzionale)</Label>
              <Textarea
                value={motivoLicenziamento}
                onChange={(e) => setMotivoLicenziamento(e.target.value)}
                placeholder="Inserisci il motivo del licenziamento..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setLicenziaDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleLicenzia} className="bg-red-600 hover:bg-red-700">
                <Trash2 className="w-4 h-4 mr-2" />
                Conferma Licenziamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Ruolo */}
      <Dialog open={modificaRuoloDialogOpen} onOpenChange={setModificaRuoloDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Modifica Ruolo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Modifica il ruolo di <strong>@{selectedDipendente?.username}</strong> in <strong>{azienda?.nome}</strong>.
            </p>
            <div className="space-y-2">
              <Label>Nuovo Ruolo</Label>
              <Select value={nuovoRuoloId} onValueChange={setNuovoRuoloId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo..." />
                </SelectTrigger>
                <SelectContent>
                  {ruoliAzienda.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setModificaRuoloDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleModificaRuolo} className="bg-blue-600 hover:bg-blue-700">
                <UserCog className="w-4 h-4 mr-2" />
                Salva Ruolo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ban Utente */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Banna Utente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Stai per bannare <strong>@{selectedDipendente?.username}</strong>.
            </p>
            <div className="space-y-2">
              <Label>Motivo del Ban *</Label>
              <Textarea
                value={banForm.motivo}
                onChange={(e) => setBanForm(prev => ({ ...prev, motivo: e.target.value }))}
                placeholder="Inserisci il motivo del ban..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo di Ban</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    checked={banForm.permanente} 
                    onChange={() => setBanForm(prev => ({ ...prev, permanente: true, durata_giorni: null }))}
                    className="w-4 h-4"
                  />
                  <span>Permanente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    checked={!banForm.permanente} 
                    onChange={() => setBanForm(prev => ({ ...prev, permanente: false, durata_giorni: 7 }))}
                    className="w-4 h-4"
                  />
                  <span>Temporaneo</span>
                </label>
              </div>
            </div>
            {!banForm.permanente && (
              <div className="space-y-2">
                <Label>Durata (giorni)</Label>
                <Input
                  type="number"
                  min={1}
                  value={banForm.durata_giorni || ''}
                  onChange={(e) => setBanForm(prev => ({ ...prev, durata_giorni: parseInt(e.target.value) || null }))}
                  placeholder="Giorni di ban"
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setBanDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleBan} className="bg-red-600 hover:bg-red-700">
                <Ban className="w-4 h-4 mr-2" />
                Conferma Ban
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Paga Stipendio */}
      <Dialog open={stipendioDialogOpen} onOpenChange={setStipendioDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-600 flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Paga Stipendio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Dipendente</Label>
              <Select value={stipendioForm.user_id} onValueChange={(v) => setStipendioForm({...stipendioForm, user_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona dipendente..." />
                </SelectTrigger>
                <SelectContent>
                  {dipendenti.map((d) => (
                    <SelectItem key={d.id} value={d.id}>@{d.username} - {d.nome} {d.cognome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Importo (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={stipendioForm.importo}
                onChange={(e) => setStipendioForm({...stipendioForm, importo: e.target.value})}
                placeholder="Es. 1500.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea
                value={stipendioForm.note}
                onChange={(e) => setStipendioForm({...stipendioForm, note: e.target.value})}
                placeholder="Es. Stipendio mese di Dicembre"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setStipendioDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handlePagaStipendio} className="bg-green-600 hover:bg-green-700">
                <Euro className="w-4 h-4 mr-2" />
                Paga Stipendio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionaleAzienda;
